import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Users, Phone, Mail, FileText, X, Loader2, Home, Euro, Calendar } from 'lucide-react';
import { Tenant, Property } from '../types';
import { useAuth } from '../hooks/useAuth';
import { useTenantsQuery, usePropertiesQuery, useAddTenantMutation, useUpdateTenantMutation, useDeleteTenantMutation } from '../hooks/useAppQuery';

// Form & Validation
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { tenantSchema, TenantFormData } from '../lib/schemas';

// Helpers
const MotionDiv = motion.div as any;

const TenantsManager: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Data Hooks
  const { data: tenants = [], isLoading: tenantsLoading } = useTenantsQuery(user?.id);
  const { data: properties = [] } = usePropertiesQuery(user?.id);
  
  // Mutations
  const addMutation = useAddTenantMutation(user?.id);
  const updateMutation = useUpdateTenantMutation(user?.id);
  const deleteMutation = useDeleteTenantMutation(user?.id);

  // Form Setup
  const { 
    register, 
    handleSubmit, 
    reset, 
    setValue,
    watch,
    formState: { errors, isSubmitting } 
  } = useForm<TenantFormData>({
    resolver: zodResolver(tenantSchema) as any,
    defaultValues: {
      status: 'Active',
      rentAmount: 0,
      depositAmount: 0
    }
  });

  const handleOpenModal = (tenant?: Tenant) => {
    if (tenant) {
      setEditingId(tenant.id);
      setValue('name', tenant.name);
      setValue('email', tenant.email);
      setValue('phone', tenant.phone);
      setValue('idNumber', tenant.idNumber || '');
      setValue('nif', tenant.nif || '');
      setValue('status', tenant.status as any);
      setValue('rentAmount', tenant.rentAmount || 0);
      setValue('depositAmount', tenant.depositAmount || 0);
      setValue('paymentDay', tenant.paymentDay);
      setValue('leaseStart', tenant.leaseStart || '');
      setValue('leaseEnd', tenant.leaseEnd || '');
      setValue('currentPropertyId', tenant.currentPropertyId || '');
      setValue('notes', tenant.notes || '');
    } else {
      setEditingId(null);
      reset();
    }
    setIsModalOpen(true);
  };

  const onSubmit = async (data: TenantFormData) => {
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ ...data, id: editingId } as Tenant);
      } else {
        await addMutation.mutateAsync(data as unknown as Tenant);
      }
      setIsModalOpen(false);
      reset();
    } catch (error) {
      console.error("Failed to save tenant", error);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this tenant?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const filteredTenants = tenants.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    t.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPropertyName = (id?: string) => {
    if (!id) return 'Unassigned';
    const prop = properties.find(p => p.id === id);
    return prop ? prop.address : 'Unknown Property';
  };

  if (tenantsLoading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-emerald-500 w-8 h-8"/></div>;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-8 pb-24">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white tracking-tight">Tenants</h2>
          <p className="text-slate-400 mt-1">{filteredTenants.length} active leases</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input 
              type="text" 
              placeholder="Search tenants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500 w-64 transition-all"
            />
          </div>
          <button 
            onClick={() => handleOpenModal()}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 shadow-lg shadow-emerald-500/20 transition-all hover:scale-105 active:scale-95"
          >
            <Plus className="w-4 h-4" /> Add Tenant
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {filteredTenants.map((tenant) => (
            <MotionDiv
              key={tenant.id}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="group bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-5 hover:border-emerald-500/30 transition-all hover:shadow-2xl"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center text-emerald-500 font-bold text-lg border border-slate-700">
                    {tenant.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-white font-bold">{tenant.name}</h3>
                    <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-0.5">
                      <Home className="w-3 h-3" /> {getPropertyName(tenant.currentPropertyId)}
                    </div>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${tenant.status === 'Active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-800 text-slate-500'}`}>
                  {tenant.status}
                </span>
              </div>

              <div className="space-y-3 mb-5">
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Mail className="w-4 h-4 text-slate-600" /> {tenant.email}
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Phone className="w-4 h-4 text-slate-600" /> {tenant.phone}
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Euro className="w-4 h-4 text-slate-600" /> {tenant.rentAmount} / month
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t border-slate-800">
                <button onClick={() => handleOpenModal(tenant)} className="flex-1 py-2 text-xs font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors">
                  Edit Details
                </button>
                <button onClick={() => handleDelete(tenant.id)} className="px-3 py-2 text-xs font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors">
                  Delete
                </button>
              </div>
            </MotionDiv>
          ))}
        </AnimatePresence>
      </div>

      {/* MODAL */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <MotionDiv 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-slate-950 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
            >
              <div className="sticky top-0 bg-slate-950 border-b border-slate-800 p-6 flex justify-between items-center z-10">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                   <Users className="w-5 h-5 text-emerald-500"/>
                   {editingId ? 'Edit Tenant' : 'New Tenant'}
                </h3>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-500 hover:text-white"><X className="w-6 h-6"/></button>
              </div>
              
              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                
                {/* Personal Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Full Name *</label>
                      <input {...register('name')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none" placeholder="John Doe" />
                      {errors.name && <span className="text-xs text-red-400">{errors.name.message}</span>}
                   </div>
                   <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Phone *</label>
                      <input {...register('phone')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none" />
                      {errors.phone && <span className="text-xs text-red-400">{errors.phone.message}</span>}
                   </div>
                </div>

                <div className="space-y-1.5">
                   <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Email Address *</label>
                   <input {...register('email')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none" />
                   {errors.email && <span className="text-xs text-red-400">{errors.email.message}</span>}
                </div>

                {/* Lease Info */}
                <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800 space-y-4">
                    <h4 className="text-sm font-bold text-white flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-500"/> Lease Details</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-bold text-slate-500 uppercase">Property</label>
                            <select {...register('currentPropertyId')} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white text-sm">
                                <option value="">Select Property...</option>
                                {properties.map(p => <option key={p.id} value={p.id}>{p.address}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-bold text-slate-500 uppercase">Monthly Rent (€)</label>
                            <input type="number" {...register('rentAmount')} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white text-sm" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-bold text-slate-500 uppercase">Lease Start</label>
                            <input type="date" {...register('leaseStart')} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white text-sm" />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-bold text-slate-500 uppercase">Lease End</label>
                            <input type="date" {...register('leaseEnd')} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white text-sm" />
                        </div>
                    </div>
                </div>

                {/* Additional Info */}
                <div className="grid grid-cols-2 gap-4">
                   <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">ID Number</label>
                      <input {...register('idNumber')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none" />
                   </div>
                   <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">NIF</label>
                      <input {...register('nif')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none" />
                   </div>
                </div>

                <div className="pt-4 flex justify-end gap-3 border-t border-slate-800">
                    <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Cancel</button>
                    <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50">
                        {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin"/> : null}
                        Save Tenant
                    </button>
                </div>
              </form>
            </MotionDiv>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TenantsManager;